import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { PersonalService } from '../services/personalService';
import '../styles/pages/personal.css';

const AREA_AVATAR = {
    'Recepción':    'a-recep',
    'Veterinaria':  'a-vet',
    'Estética':     'a-groom',
    'Guardería':    'a-care',
    'Administración': 'a-admin',
    'Transporte':   'a-bus',
};

const ESTATUS_LABEL = {
    activo:      'Activo',
    vacaciones:  'Vacaciones',
    incidencia:  'Incidencia',
    incapacidad: 'Incapacidad',
    baja:        'Baja',
};

const ESTATUS_CLASS = {
    activo:      'pe-st-act',
    vacaciones:  'pe-st-vac',
    incidencia:  'pe-st-inc',
    incapacidad: 'pe-st-inc',
    baja:        'pe-st-baja',
};

function calcAntiguedad(fechaIngreso) {
    if (!fechaIngreso) return '';
    const from = new Date(fechaIngreso);
    const now = new Date();
    let years = now.getFullYear() - from.getFullYear();
    let months = now.getMonth() - from.getMonth();
    if (months < 0) { years -= 1; months += 12; }
    if (years === 0 && months === 0) return 'Reciente';
    const yStr = years > 0 ? `${years} año${years !== 1 ? 's' : ''}` : '';
    const mStr = months > 0 ? `${months} mes${months !== 1 ? 'es' : ''}` : '';
    return [yStr, mStr].filter(Boolean).join(' ');
}

function getInitials(nombre) {
    if (!nombre) return '?';
    const parts = nombre.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

const FILTROS = [
    { id: 'todos',      l: 'Todos' },
    { id: 'activo',     l: 'Activos' },
    { id: 'vacaciones', l: 'Vacaciones' },
    { id: 'incidencia', l: 'Con incidencia' },
];

export default function PersonalDirectory() {
    const { authorizedFetch, role } = useAuth();
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [q, setQ] = useState('');
    const [filtro, setFiltro] = useState('todos');

    useEffect(() => {
        if (role !== 'admin') { navigate('/settings'); return; }
        fetchEmployees();
    }, [role]); // eslint-disable-line

    const fetchEmployees = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await PersonalService.getAll(authorizedFetch);
            setEmployees(data);
        } catch (err) {
            setError('No se pudo cargar el directorio.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const lista = useMemo(() => employees.filter(e => {
        if (q) {
            const hay = `${e.nombre} ${e.puesto} ${e.area}`.toLowerCase();
            if (!hay.includes(q.toLowerCase())) return false;
        }
        if (filtro === 'activo' && e.estatus !== 'activo') return false;
        if (filtro === 'vacaciones' && e.estatus !== 'vacaciones') return false;
        if (filtro === 'incidencia' && !['incapacidad', 'incidencia'].includes(e.estatus)) return false;
        return true;
    }), [q, filtro, employees]);

    const activos = employees.filter(e => e.estatus === 'activo').length;

    return (
        <>
            <Navigation />
            <div className="pe-container">
                <button className="pe-back-link" onClick={() => navigate('/settings')}>
                    ‹ Configuración
                </button>

                <div className="pe-ph">
                    <div>
                        <h1 className="pe-ph-title">Personal</h1>
                        <p className="pe-ph-sub">{activos} activos · {employees.length} colaboradores</p>
                    </div>
                    <span className="pe-ph-badge">{employees.length}</span>
                </div>

                <div className="pe-search">
                    <span style={{ color: '#9ca3af', fontSize: 15 }}>🔍</span>
                    <input
                        placeholder="Buscar por nombre, puesto o área…"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                    />
                </div>

                <div className="pe-chips">
                    {FILTROS.map(f => (
                        <button key={f.id} className={`pe-chip ${filtro === f.id ? 'on' : ''}`} onClick={() => setFiltro(f.id)}>
                            {f.l}
                        </button>
                    ))}
                </div>

                {loading && <div className="pe-loading">Cargando…</div>}
                {error && <div className="pe-toast error">{error}</div>}

                {!loading && (
                    <div className="pe-emp-list">
                        {lista.map(e => (
                            <button key={e._id} className="pe-emp-card" onClick={() => navigate(`/settings/personal/${e._id}`)}>
                                <div className={`pe-avatar ${AREA_AVATAR[e.area] || ''}`}>
                                    {getInitials(e.nombre)}
                                </div>
                                <div className="pe-emp-info">
                                    <div className="pe-emp-name">{e.nombre}</div>
                                    <div className="pe-emp-meta">{e.puesto} · {e.area}</div>
                                </div>
                                <div className="pe-emp-right">
                                    <span className={`pe-st ${ESTATUS_CLASS[e.estatus] || 'pe-st-act'}`}>
                                        {ESTATUS_LABEL[e.estatus] || e.estatus}
                                    </span>
                                    <span className="pe-emp-meta" style={{ fontSize: '.7rem' }}>
                                        {calcAntiguedad(e.ingreso)}
                                    </span>
                                </div>
                            </button>
                        ))}
                        {!lista.length && <div className="pe-empty">Sin resultados para tu búsqueda.</div>}
                    </div>
                )}

                <button className="pe-add-btn" onClick={() => navigate('/settings/personal/new')}>
                    ＋ Dar de alta empleado
                </button>
            </div>
        </>
    );
}
